import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import type { SvgIconComponent } from '@mui/icons-material';

interface SingleSelectProps {
    options: { icon?: SvgIconComponent; label: string; value: string }[];
    value: string;
    onChange: (value: string) => void;
    multi?: false;
    disabledOptions?: string[];
}

interface MultiSelectProps {
    options: { icon?: SvgIconComponent; label: string; value: string }[];
    value: string[];
    onChange: (value: string[]) => void;
    multi: true;
    disabledOptions?: string[];
}

type ToggleGroupProps = SingleSelectProps | MultiSelectProps;

export function ToggleGroup(props: ToggleGroupProps) {
    const { options, multi, disabledOptions } = props;

    const isSelected = (optionValue: string) => {
        if (multi) return (props.value as string[]).includes(optionValue);
        return optionValue === props.value;
    };

    const isDisabled = (optionValue: string) => disabledOptions?.includes(optionValue) ?? false;

    const handleClick = (optionValue: string) => {
        if (isDisabled(optionValue)) return;
        if (multi) {
            const current = props.value as string[];
            const onChange = props.onChange as (value: string[]) => void;
            if (current.includes(optionValue)) {
                onChange(current.filter((v) => v !== optionValue));
            } else {
                onChange([...current, optionValue]);
            }
        } else {
            (props.onChange as (value: string) => void)(optionValue);
        }
    };

    return (
        <Container>
            {options.map((option) => (
                <ToggleButton
                    key={option.value}
                    selected={isSelected(option.value)}
                    disabled={isDisabled(option.value)}
                    onClick={() => handleClick(option.value)}
                >
                    {option.icon && <option.icon sx={{ fontSize: 14 }} />}
                    {option.label}
                </ToggleButton>
            ))}
        </Container>
    );
}

const Container = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
});

const ToggleButton = styled('button')<{ selected: boolean }>(({ selected }) => ({
    padding: '0px 10px',
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'inherit',
    height: 24,
    borderRadius: 30,
    border: selected ? `2px solid ${Colors.turquoise}` : `1px solid ${Colors.lightGrey}`,
    backgroundColor: selected ? Colors.turquoise : Colors.faintGrey,
    color: selected ? Colors.white : Colors.mediumGrey,
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.15s ease',
    '&:focus, &:focus-visible': {
        outline: 'none',
        boxShadow: 'none',
    },
    '&:hover': {
        borderColor: Colors.turquoise,
    },
    '&:disabled': {
        cursor: 'default',
        opacity: 0.85,
        borderColor: selected ? Colors.turquoise : Colors.lightGrey,
    },
    '&:disabled:hover': {
        borderColor: selected ? Colors.turquoise : Colors.lightGrey,
    },
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
}));
